import Styles from './ProductListCard.module.scss';
import React, { useContext, useEffect, useState } from 'react';
import { Popover } from '@mui/material';
import FeatherIcon from 'feather-icons-react';

// Components import.
import { ViewContext } from 'components/lib';
import { Button } from '@mui/material';
import { MilitaryTech } from '@mui/icons-material';
import Badge, { BADGE_TYPES } from 'components/global/CardBadge/CardBadge';

/**
 * 
 * @param {boolean} minimal if true then shows a shrunk version of the card
 * @returns 
 */

const ProductListCard = ({ tooltipMessage, data, minimal = false, isInternship = false, ...props }) => {

    const viewContext = useContext(ViewContext)

    const [anchorEl, setAnchorEl] = useState(null);

    const isPopoverOpen = Boolean(anchorEl);

    const chipColorMap = {
        "Completed": Styles.completedChip,
        "Continue Course": Styles.continueCourseChip,
        "Trial Ended": Styles.trialEndedChip,
        "Start Course": Styles.startCourse,
        "In Certifications": Styles.inCertification,
        "License Expired": Styles.trialEndedChip,
        "Continue Certificate": Styles.continueCourseChip,
        "In Internships": Styles.inInternships
    };

    // Close popover on scroll
    useEffect(() => {
        if (!isPopoverOpen) return;

        const handleScroll = () => {
            setAnchorEl(null);
        };

        // capture = true → catches scroll from any container
        window.addEventListener('scroll', handleScroll, true);

        return () => {
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [isPopoverOpen]);

    const resolveBadgeConfig = (data) => {
        if (!data) return null;

        if (data.is_new) {
            return {
                type: BADGE_TYPES.INFO,
                label: 'New',
            };
        }

        switch (data.subscriptionStatus) {
            case 'trial':
                return {
                    type: BADGE_TYPES.SUCCESS,
                    label: 'On Trial',
                };

            case 'trial_expired':
                return {
                    type: BADGE_TYPES.ERROR,
                    label: 'Purchase Required',
                };

            case 'disabled':
                return {
                    type: BADGE_TYPES.WARN,
                    label: 'Pending Renewal',
                };

            default:
                return null;
        }
    }

    const badgeConfig = resolveBadgeConfig(data);

    const buttonChipClass =
        data.isFailed
            ? Styles.failedChip
            : chipColorMap[data.buttonText] || Styles.startCourse;

    //converting the due date into dd-mm-yyy format
    const dueDate = data?.due_date && new Date(data?.due_date);
    const parsedDueDate = data?.due_date && dueDate.toLocaleDateString(
        'en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }
    );
    const purchaseDate = data?.purchase_date && new Date(data?.purchase_date);
    const parsedPurchaseDate = data?.purchase_date && purchaseDate.toLocaleDateString(
        'en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }
    );

    const tooltipText =
        isInternship
            ? viewContext.t("Components.ProductGridCard.internshipPrompt")
            : viewContext.t("Components.ProductGridCard.certPrompt");

    return (
        <>
            <div
                id="gridAncestor"
                className={Styles.container}
                data-testid="ProductListCard-gridAncestor"
                onClick={(e) => {
                    e.preventDefault();
                    // If card should show popover
                    if (isInternship || data?.hasOwnProperty("cert_id")) {
                        setAnchorEl(e.currentTarget);
                    }
                    // Otherwise normal navigation
                    props.onClick?.(data, e);
                }}
            >

                <div data-testid="ProductCard-card" className={Styles.card}>
                    {/* TOP BADGE */}
                    {props.showBadge && badgeConfig && (
                        <Badge type={badgeConfig.type}>
                            {badgeConfig.label}
                        </Badge>
                    )}

                    <div data-testid="ProductCard-inner" className={Styles.cardInnerList}>
                        {/* IMAGE LEFT */}
                        <div data-testid="ProductCard-image-wrapper" className={Styles.imageWrapperList}>
                            <div data-testid="ProductCard-image-box" className={Styles.imageBoxList}>
                                <img src={`${process.env.REACT_APP_GLOBAL_MEDIA_URL}${data.icon || data.image}`} alt={data.name} />
                            </div>
                        </div>

                        {/* CONTENT */}
                        <div data-testid="ProductCard-content" className={Styles.cardContentList}>

                            {/* TOP ROW inside content */}
                            <div data-testid="ProductCard-top" className={Styles.topRowList}>
                                {data.badgeText && <div className={Styles.badge}>{data.badgeText}</div>}
                                {data?.due_date && (
                                    <span data-testid="ProductCard-due-date" title={viewContext.t('Components.ProductGridCard.dueDate')} className={Styles.dueDate}>
                                        <FeatherIcon
                                            icon="clock"
                                            size='12px'
                                            style={{ color: 'grey', marginRight: '4px' }}
                                        />
                                        {parsedPurchaseDate && parsedDueDate ? `${parsedPurchaseDate} - ${parsedDueDate}` : parsedDueDate}
                                    </span>
                                )}
                            </div>

                            <h3 data-testid="ProductCard-title" className={Styles.title}>{data.name}</h3>
                            {!minimal && (
                                <p className={Styles.description} data-testid="ProductCard-description">{data.descr || data.description}</p>
                            )}

                            {/* METRICS, BUTTONS, BADGES */}
                            <div data-testid="ProductCard-metrics" className={Styles.metricsRow}>
                                {props.showInfoChips && data.score !== undefined && (
                                    <div data-testid="ProductCard-score-box" className={Styles.scoreBox}>
                                        <div data-testid="ProductCard-score-text" className={Styles.scoreText}>
                                            <span className={Styles.scoreLabel}>{viewContext.t('Components.ProductGridCard.score')}</span>
                                            <span className={Styles.scoreValue}>
                                                {data.score}/{data.totalScore ?? 10}
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {props.showProgressBar && (
                                    <div data-testid="ProductCard-progress-box" className={Styles.progressBox}>
                                        <div data-testid="ProductCard-progress-header" className={Styles.progressHeader}>
                                            <span>{viewContext.t('Components.ProductGridCard.progress')}</span>
                                            <span className={Styles.progressValue}>{data.completion_percentage}%</span>
                                        </div>

                                        <div data-testid="ProductCard-progress-header" className={Styles.progressBar}>
                                            <div data-testid="ProductCard-progress-fill"
                                                className={Styles.progressFill}
                                                style={{ width: `${data.completion_percentage}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                )}

                                {/* STATUS + BADGES */}
                                <div data-testid="ProductCard-status-row" className={Styles.statusRow}>
                                    <div data-testid="ProductCard-status-button" className={Styles.buttonContainer}>
                                        <span
                                            data-testid="ProductCard-status-chip"
                                            className={`${Styles.statusChip} ${buttonChipClass}`}
                                            onClick={() => !data.isDisabled && props.onClick?.(data.id)}
                                            style={{
                                                cursor: data.isDisabled ? "not-allowed" : "pointer",
                                                opacity: data.isDisabled ? 0.6 : 1,
                                                pointerEvents: data.isDisabled ? "none" : "auto",
                                                width: "auto",
                                                textAlign: "center"
                                            }}
                                        >
                                            {viewContext.t(`Components.ProductGridCard.button.${data.buttonText}`)}
                                        </span>
                                    </div>
                                    {!!props?.showMedalChips && (
                                        <div data-testid="ProductCard-badge-list" className={Styles.badgeList}>
                                            <MilitaryTech fontSize='large' sx={{ color: '#F7C706', opacity: data.badge === 3 ? 1 : 0.3 }} />
                                            <MilitaryTech fontSize='large' sx={{ color: '#8D9297', opacity: data.badge === 2 ? 1 : 0.3 }} />
                                            <MilitaryTech fontSize='large' sx={{ color: '#CE8946', opacity: data.badge === 1 ? 1 : 0.3 }} />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div >

            <Popover
                open={isPopoverOpen}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{
                    vertical: 'center',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'center',
                    horizontal: 'left',
                }}
                disableRestoreFocus
                disableScrollLock
            >
                <div className={Styles.tooltipMain}>
                    <p className={Styles.frmCert}>
                        {tooltipText}
                    </p>

                    {props?.isPartOfOtherProducts?.map((item, index) => (
                        <div key={index} style={{ width: '100%' }}>
                            <Button
                                fullWidth
                                onClick={() => {
                                    setAnchorEl(null);
                                    props?.onCertTooltipClick?.(
                                        item?.id,
                                        item?.isInternship
                                    );
                                }}
                                disabled={item?.isExpired}
                            >
                                {item?.name}
                            </Button>
                        </div>
                    ))}
                </div>
            </Popover>
        </>
    );
};

export default ProductListCard;